import { useEffect, useRef, useState } from 'react'
import { Camera, Loader2 } from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'
import { authService } from '../../../services/api'
import Avatar from '../../../components/Avatar/Avatar'
import SignatureCanvas from '../../../components/SignatureCanvas/SignatureCanvas'
import Toast from '../../../components/Toast/Toast'
import { useToast } from '../../../hooks/useToast'
import ErpPeriodDropdown from '../../../components/ERP/ErpPeriodDropdown/ErpPeriodDropdown'
import { COUNCIL_UFS, PROFESSIONAL_COUNCILS } from '../../../utils/professionalCouncil'
import './PerfilPage.css'

function formatDocument(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 14)
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
  }
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
}

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')
  } catch (error) {
    return null
  }
}

function mergeSessionUser(current, next) {
  const stored = readStoredUser()
  const base = stored || current || {}
  return {
    ...base,
    ...(next || {}),
    organization_schema: base.organization_schema,
    organization_label: base.organization_label,
  }
}

function PerfilPage() {
  const { user, updateUser } = useAuth()
  const fileRef = useRef(null)
  const [form, setForm] = useState({
    name: '',
    phone: '',
    address: '',
    cpfCnpj: '',
    professionalCouncil: '',
    professionalRegistry: '',
    professionalCouncilUf: '',
    password: '',
    confirmPassword: '',
  })
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '')
  const [signatureUrl, setSignatureUrl] = useState(user?.signature_url || '')
  const [drawing, setDrawing] = useState(false)
  const [savingSignature, setSavingSignature] = useState(false)
  const [accessGroup, setAccessGroup] = useState(user?.access_group_name || '')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const { toast, showToast, hideToast } = useToast()

  useEffect(() => {
    let active = true
    authService.getProfile()
      .then((response) => {
        if (!active || !response?.success) return
        const profile = response.data || {}
        setForm((current) => ({
          ...current,
          name: profile.name || '',
          phone: profile.phone || '',
          address: profile.address || '',
          cpfCnpj: formatDocument(profile.cpf_cnpj),
          professionalCouncil: profile.professional_council || '',
          professionalRegistry: profile.professional_registry || '',
          professionalCouncilUf: profile.professional_council_uf || '',
        }))
        setAvatarUrl(profile.avatar_url || '')
        setSignatureUrl(profile.signature_url || '')
        setAccessGroup(profile.access_group_name || '')
        updateUser(mergeSessionUser(null, profile))
      })
      .catch(() => {
        if (active) setError('Não foi possível carregar o perfil.')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
    // O perfil é carregado uma vez ao abrir a página.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (loading) return
    const target = window.location.hash.replace('#', '')
    if (target !== 'perfil-classe' && target !== 'perfil-assinatura') return
    if (target === 'perfil-assinatura') setDrawing(true)
    const timer = window.setTimeout(() => {
      document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 60)
    return () => window.clearTimeout(timer)
  }, [loading])

  const onChange = (event) => {
    const { name, value } = event.target
    setForm((current) => ({
      ...current,
      [name]: name === 'cpfCnpj' ? formatDocument(value) : value,
    }))
  }

  const onPhoto = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('Escolha um arquivo de imagem.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('A imagem deve ter no máximo 5MB.')
      return
    }

    setUploading(true)
    setError('')
    try {
      const response = await authService.uploadAvatar(file)
      const nextUrl = response?.data?.avatar_url || ''
      setAvatarUrl(nextUrl)
      updateUser(mergeSessionUser(user, { ...(response?.data || {}), avatar_url: nextUrl }))
      showToast('Foto atualizada.', 'success')
    } catch (uploadError) {
      const uploadMessage = uploadError.response?.data?.message || 'Não foi possível enviar a foto.'
      setError(uploadMessage)
      showToast(uploadMessage, 'error')
    } finally {
      setUploading(false)
    }
  }

  const onSubmit = async (event) => {
    event.preventDefault()
    setError('')

    if (!form.name.trim()) {
      setError('Informe o nome.')
      return
    }
    if (form.password && form.password.length < 6) {
      setError('A nova senha precisa ter pelo menos 6 caracteres.')
      return
    }
    if (form.password !== form.confirmPassword) {
      setError('A confirmação da senha não confere.')
      return
    }
    const councilParts = [form.professionalCouncil, form.professionalRegistry.trim(), form.professionalCouncilUf].filter(Boolean)
    if (councilParts.length > 0 && councilParts.length < 3) {
      setError('Preencha o conselho, o número do registro e a UF.')
      return
    }

    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      address: form.address.trim(),
      cpfCnpj: form.cpfCnpj.replace(/\D/g, '') || null,
      professionalCouncil: form.professionalCouncil,
      professionalRegistry: form.professionalRegistry.trim(),
      professionalCouncilUf: form.professionalCouncilUf,
    }
    if (form.password) payload.password = form.password

    setSaving(true)
    try {
      const response = await authService.updateProfile(payload)
      const profile = response?.data || {}
      setAccessGroup(profile.access_group_name || accessGroup)
      setForm((current) => ({ ...current, password: '', confirmPassword: '' }))
      updateUser(mergeSessionUser(user, profile))
      window.dispatchEvent(new Event('wfmed-profile-updated'))
      showToast('Perfil salvo.', 'success')
    } catch (saveError) {
      const saveMessage = saveError.response?.data?.message || 'Não foi possível salvar o perfil.'
      setError(saveMessage)
      showToast(saveMessage, 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="perfil-page">
        <p className="perfil-status">Carregando perfil...</p>
      </div>
    )
  }

  return (
    <div className="perfil-page">
      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
      />
      <header className="perfil-header">
        <h1>Meu perfil</h1>
        <p>Atualize sua foto e as informações da sua conta.</p>
      </header>

      <form className="perfil-card" onSubmit={onSubmit}>
        <div className="perfil-photo">
          <button
            type="button"
            className="perfil-photo-button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            <Avatar
              name={form.name || user?.name}
              email={user?.email}
              imageUrl={avatarUrl}
              size="lg"
            />
            <span className="perfil-photo-badge">
              {uploading ? <Loader2 size={16} className="perfil-spin" /> : <Camera size={16} />}
            </span>
          </button>
          <div>
            <strong>{form.name || user?.name}</strong>
            <p>{user?.email}</p>
            <button
              type="button"
              className="perfil-link"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Enviando foto...' : 'Alterar foto'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onPhoto}
            />
          </div>
        </div>

        <div className="perfil-grid">
          <label className="perfil-field">
            <span>Nome</span>
            <input name="name" value={form.name} onChange={onChange} required />
          </label>
          <label className="perfil-field">
            <span>E-mail</span>
            <input value={user?.email || ''} readOnly />
          </label>
          <label className="perfil-field">
            <span>Telefone</span>
            <input name="phone" value={form.phone} onChange={onChange} placeholder="(00) 00000-0000" />
          </label>
          <label className="perfil-field">
            <span>CPF ou CNPJ</span>
            <input name="cpfCnpj" value={form.cpfCnpj} onChange={onChange} inputMode="numeric" />
          </label>
          <label className="perfil-field perfil-field--wide">
            <span>Endereço</span>
            <textarea name="address" value={form.address} onChange={onChange} rows={3} />
          </label>
          <div className="perfil-council" id="perfil-classe">
            <label className="perfil-field">
              <span>Conselho</span>
              <ErpPeriodDropdown
                className="perfil-dropdown"
                label=""
                align="left"
                portal
                ariaLabel="Conselho profissional"
                value={form.professionalCouncil}
                options={[{ value: '', label: 'Selecione' }, ...PROFESSIONAL_COUNCILS]}
                onChange={(value) => setForm((current) => ({ ...current, professionalCouncil: value }))}
              />
            </label>
            <label className="perfil-field">
              <span>Nº do registro</span>
              <input
                name="professionalRegistry"
                value={form.professionalRegistry}
                onChange={onChange}
                maxLength={20}
                placeholder="123456"
                autoComplete="off"
              />
            </label>
            <label className="perfil-field">
              <span>UF do conselho</span>
              <ErpPeriodDropdown
                className="perfil-dropdown"
                label=""
                align="left"
                portal
                ariaLabel="UF do conselho"
                searchable
                value={form.professionalCouncilUf}
                options={[{ value: '', label: 'UF' }, ...COUNCIL_UFS.map((uf) => ({ value: uf, label: uf }))]}
                onChange={(value) => setForm((current) => ({ ...current, professionalCouncilUf: value }))}
              />
            </label>
            <p className="perfil-hint">No prontuário aparece junto da assinatura, por exemplo CRM/SP 123456.</p>
          </div>
          <label className="perfil-field">
            <span>Grupo de acesso</span>
            <input value={accessGroup || 'Ainda não atribuído'} readOnly />
          </label>
          <label className="perfil-field">
            <span>Nova senha</span>
            <input
              name="password"
              type="password"
              value={form.password}
              onChange={onChange}
              autoComplete="new-password"
              placeholder="Deixe em branco para manter"
            />
          </label>
          <label className="perfil-field">
            <span>Confirmar nova senha</span>
            <input
              name="confirmPassword"
              type="password"
              value={form.confirmPassword}
              onChange={onChange}
              autoComplete="new-password"
            />
          </label>
        </div>

        {error ? <p className="perfil-feedback perfil-feedback--error">{error}</p> : null}

        <div className="perfil-actions">
          <button type="submit" className="perfil-save" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar perfil'}
          </button>
        </div>
      </form>

      <section className="perfil-card perfil-signature" id="perfil-assinatura">
        <h2>Minha assinatura</h2>
        <p>Essa assinatura entra na impressão do prontuário e das evoluções.</p>
        {signatureUrl && !drawing && (
          <img className="perfil-signature-preview" src={signatureUrl} alt="Assinatura salva" />
        )}
        {drawing && (
          <SignatureCanvas
            width={520}
            height={180}
            onCancel={() => setDrawing(false)}
            onSave={async (dataUrl) => {
              setSavingSignature(true)
              setError('')
              try {
                const response = await authService.saveSignature(dataUrl)
                const next = response?.data?.signature_url || ''
                setSignatureUrl(next)
                updateUser(mergeSessionUser(user, response?.data || { signature_url: next }))
                setDrawing(false)
                window.dispatchEvent(new Event('wfmed-profile-updated'))
                showToast('Assinatura salva.', 'success')
              } catch (err) {
                setError(err.response?.data?.message || 'Não foi possível salvar a assinatura')
              } finally {
                setSavingSignature(false)
              }
            }}
          />
        )}
        <div className="perfil-signature-actions">
          <button type="button" className="perfil-save" onClick={() => setDrawing((current) => !current)} disabled={savingSignature}>
            {drawing ? 'Fechar' : signatureUrl ? 'Refazer assinatura' : 'Desenhar assinatura'}
          </button>
          {signatureUrl && !drawing && (
            <button
              type="button"
              className="perfil-remove"
              disabled={savingSignature}
              onClick={async () => {
                setSavingSignature(true)
                setError('')
                try {
                  const response = await authService.deleteSignature()
                  setSignatureUrl('')
                  setDrawing(false)
                  updateUser(mergeSessionUser(user, response?.data || { signature_url: null }))
                  window.dispatchEvent(new Event('wfmed-profile-updated'))
                  showToast('Assinatura removida.', 'success')
                } catch (err) {
                  setError(err.response?.data?.message || 'Não foi possível remover a assinatura')
                } finally {
                  setSavingSignature(false)
                }
              }}
            >
              Remover
            </button>
          )}
        </div>
      </section>
    </div>
  )
}

export default PerfilPage
